<script>
import Alerts from './Alerts.vue'
import FormUpload from '@/components/FileUpload.vue'

export default {
    name: 'Documents',
    components: {FormUpload, Alerts},
    props: ['code'],

    data() {
        return {
            axios: this.$http,
            base_url: '',
            erp: {budgeting: []},

            documents: [],

            showSummary: true,
            allowEditing: false
        }
    },
    mounted() {
        this.loadErp()
        this.loadDocuments()

    },
    computed: {
        groupedDocuments() {
            const groups = {}

            // Grouping logic
            this.documents.forEach(doc => {
                const category = doc.category || 'Other'
                if (!groups[category]) {
                    groups[category] = {
                        documents: [],
                        totalAmount: 0
                    }
                }
                groups[category].documents.push(doc)
                groups[category].totalAmount += Number(doc.amount || 0)
            })

            // Convert to entries, sort by document count
            const sortedEntries = Object.entries(groups)
                .sort((a, b) => b[1].documents.length - a[1].documents.length)

            // Rebuild sorted object (optional but keeps compatibility)
            const sortedGroups = {}
            sortedEntries.forEach(([key, value]) => {
                sortedGroups[key] = value
            })

            return sortedGroups
        },

        getReceiptsSummary() {
            const summary = {}

            this.documents.forEach(doc => {
                const category = doc.category || 'Other'
                const amount = parseFloat(doc.amount) || 0

                if (!summary[category]) {
                    summary[category] = {
                        total: 0,
                        count: 0
                    }
                }

                summary[category].total += amount
                summary[category].count += 1
            })

            return summary
        }
    },

    methods: {
        loadErp() {
            this.axios.get(`${this.base_url}/request/json/${this.code}`).then(response => {
                this.erp = response.data.data
            })
        },

        getBudgetForCategory(code) {
            const match = this.erp.budgeting.find(a => a.code === code);
            if (match) {
                return match;
            }
            return {
                item: "Other"
            }
        },

        loadDocuments() {
            this.wait = true
            this.clear()
            this.axios.get(`${this.base_url}/request/${this.code}/docs`).then(response => {
                this.documents = response.data.data
            }).catch(error => {
                this.showError(error)
            }).then(() => {
                this.wait = false
            })
        },

        updateDocuments() {
            this.wait = true
            this.clear()
            window.scroll(0, 0)

            this.axios.post(`${this.base_url}/request/${this.code}/docs`, {documents: this.documents}).then(response => {
                this.success = "Documents updates successfully"
                this.allowEditing = false
                this.loadDocuments()
            }).catch(error => {
                this.showError(error)
            }).then(() => {
                this.wait = false
            })
        },

        deleteDoc(doc) {
            this.wait = true
            if (confirm(`Delete this document? ${doc.title}`)) {
                this.axios.post(`${this.base_url}/request/${this.code}/docs`, {
                    delete_id: doc.id
                }).then(() => {
                    this.loadDocuments()
                }).catch(error => {
                    this.showError(error)
                }).then(() => {
                    this.wait = false
                })
            }
        },

        normalizeNumber(value) {
            if (value === null || value === undefined || value === '') return 0;
            const cleaned = String(value).replace(/,/g, '').trim();
            const n = Number(cleaned);
            return Number.isFinite(n) ? n : 0;
        },

        // returns { text, cls } for status column
        getStatus(category, total) {
            const totalNum = this.normalizeNumber(total);
            const actualNum = this.normalizeNumber(this.getBudgetForCategory(category).actual);

            // floating equality tolerance
            const EPS = 0.0001;
            if (Math.abs(totalNum - actualNum) < EPS) {
                return {text: '✅ Match', cls: 'text-success'};
            }

            if (totalNum > actualNum) {
                return {text: '🚨 Over', cls: 'text-danger'};
            }

            // totalNum < actualNum
            return {text: '🟡 Below', cls: 'text-warning'};
        }
    }
}
</script>

<template>
    <div class="p-4 bg-white shadow-sm rounded-lg mb-3">
        <div v-if="erp.code" class="mb-3">
            <h5 class="font-weight-bold">ERP: {{ erp.code }} | <span class="font">{{ money(erp.amount) }}</span></h5>
            <span class="status float-right" :class="erp.status">{{ erp.status }}</span>
            <p>{{ erp.title }}</p>
        </div>

        <alerts :_success="success" :_error="error" :_wait="wait"></alerts>

        <!-- Upload section -->
        <br>
        <FormUpload :code="code" @reloadDocuments="loadDocuments"></FormUpload>

        <!-- Receipts summary -->
        <section v-if="erp.type!='card'">
            <h5 class="mt-4 font-weight-bold">Receipts Summary</h5>
            <div class="custom-control custom-switch float-right">
                <input type="checkbox" v-model="showSummary" class="custom-control-input" id="show_summary">
                <label class="custom-control-label" for="show_summary">{{ showSummary ? 'Hide' : 'Show' }}
                    Summary</label>
            </div>
            <p class="text-muted">A summary of uploaded receipts categorized by the ERP budget items</p>

            <table class="table table-sm table-bordered table-hover" v-if="showSummary">
                <thead class="thead-light">
                <tr>
                    <th>#</th>
                    <th>Category</th>
                    <th class="text-right">Receipts</th>
                    <th class="text-right">Actual</th>
                    <th class="text-right">Receipts Amnt</th>
                    <th class="text-right">Variance</th>
                    <th v-if="erp.budgeted_at">Status</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(data, category, index) in getReceiptsSummary" :key="category">
                    <td>{{ index + 1 }}</td>
                    <td class="w-50">{{ getBudgetForCategory(category).item }}</td>
                    <td class="text-right">{{ data.count }}</td>
                    <td class="text-right font-weight-bold">{{ getBudgetForCategory(category).actual || 0 }}</td>
                    <td class="text-right font-weight-bold">
                        {{ Number(data.total).toLocaleString() }} /=
                    </td>

                    <td class="text-right"
                        :class="{ 'text-danger': (getBudgetForCategory(category).variance) < 0 }">
                        <i>{{
                                Math.round((getBudgetForCategory(category).actual) - (data.total)).toLocaleString()
                            }}</i>
                    </td>

                    <!-- New creative Status column -->
                    <td v-if="erp.budgeted_at" title="Comparison between amount spent (actuals) vs receipt total">
                            <span :class="getStatus(category, data.total).cls" class="small">
                                {{ getStatus(category, data.total).text }}
                            </span>
                    </td>
                </tr>

                </tbody>
                <tfoot>
                <tr>
                    <th></th>
                    <th colspan="3">Grand Total</th>
                    <th class="text-right font-weight-bold">
                        <u>
                            {{
                                Object.values(getReceiptsSummary)
                                    .reduce((sum, cat) => sum + cat.total, 0)
                                    .toLocaleString()
                            }} /=
                        </u>
                    </th>
                </tr>
                </tfoot>
            </table>
        </section>

        <!-- Uploaded documents -->
        <section class="mb-5">
            <h5 class="mt-4 font-weight-bold">Receipts</h5>
            <div v-if="documents.length" class="custom-control custom-switch float-right">
                <input type="checkbox" v-model="allowEditing" class="custom-control-input" id="edit_categories">
                <label class="custom-control-label" for="edit_categories">Edit
                    Categories</label>
            </div>
            <p class="text-muted">ERP has a total of {{ documents.length }} documents uploaded</p>

            <div class="accordion mt-4" id="documentsAccordion">
                <div v-for="(group, category, index) in groupedDocuments" :key="category" class="card">
                    <!-- Accordion Header -->
                    <div class="card-header d-flex justify-content-between align-items-center" :id="'heading' + index">
                        <h5 class="mb-0">
                            <a href="#"
                               class="btn-link"
                               type="button"
                               data-toggle="collapse"
                               :data-target="'#collapse' + index"
                               aria-expanded="true"
                               :aria-controls="'collapse' + index">
                                {{ getBudgetForCategory(category).item  }}
                            </a>
                        </h5>
                        <span class="text-muted">
                              Total:
                              <span :class="group.totalAmount > 0 ? 'text-success font-weight-bold' : 'text-muted'">
                                {{ Number(group.totalAmount || 0).toLocaleString() }} /=
                              </span>
                            </span>
                    </div>

                    <!-- Accordion Body -->
                    <div
                        :id="'collapse' + index"
                        class="collapse"
                        :class="{ show: index === 0 }"
                        :aria-labelledby="'heading' + index"
                        data-parent="#documentsAccordion"
                    >
                        <div class="card-body">
                            <div class="row">
                                <div
                                    v-for="doc in group.documents"
                                    :key="doc.id"
                                    class="col-md-6 mb-4">
                                    <div class="shadow-sm rounded-lg d-flex flex-column h-100 border">
                                        <!-- Preview -->
                                        <div class="flex-fill d-flex flex-column">
                                            <div class="flex-fill">
                                                <a :href="doc.url" target="_blank">
                                                    <img
                                                        v-if="doc.type === 'img'"
                                                        :src="doc.url"
                                                        class="img-fluid rounded-top"
                                                        style="max-height: 300px; object-fit: contain;"
                                                        alt="Image Preview"
                                                    />
                                                    <embed
                                                        v-else-if="doc.type === 'pdf'"
                                                        :src="doc.url"
                                                        type="application/pdf"
                                                        width="100%"
                                                        style="min-height: 280px;"
                                                    />
                                                    <div v-else class="text-center text-muted py-5">
                                                        No Preview Available
                                                    </div>
                                                </a>
                                            </div>

                                            <!-- Metadata -->
                                            <div class="bg-light p-3 small">
                                                <h6 class="mb-2 font-weight-bold">
                                                    <a :href="doc.url" target="_blank">{{ doc.title }}</a>
                                                </h6>

                                                <div>
                                                    <strong>Uploaded:</strong>
                                                    {{ day(doc.created_at).format('MMM DD, YYYY') }}
                                                </div>

                                                <div>
                                                    <strong>Amount:</strong>
                                                    <span
                                                        :class="doc.amount > 0 ? 'text-success font-weight-bold h6' : 'text-muted'"
                                                    >
                                                    {{ doc.amount ? Number(doc.amount).toLocaleString() : '0' }} /=
                                                  </span>
                                                </div>

                                                <div>
                                                    <strong>Path:</strong>
                                                    <span class="text-truncate" style="max-width: 100%;"
                                                          :title="doc.path">
                                                        <small class="text-muted">{{ doc.path }}</small>
                                                    </span>
                                                </div>

                                                <div v-show="allowEditing" class="my-4 form-row">
                                                    <div class="form-group col-md-6">
                                                        <input type="text" v-model="doc.title" required
                                                               class="form-control" placeholder="Enter text">
                                                    </div>
                                                    <div class="form-group col-md-6">
                                                        <input type="number" min="0" v-model="doc.amount"
                                                               class="form-control" placeholder="0">
                                                    </div>
                                                    <div class="form-group col-md-12">
                                                        <select class="form-control" v-model="doc.category" required>
                                                            <option v-for="budget in erp.budgeting" :value="budget.code"
                                                                    :key="budget.code">
                                                                {{ budget.item }}
                                                            </option>
                                                            <option value="other">Other</option>
                                                        </select>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>

                                        <!-- Actions -->
                                        <div class="p-2 border-top text-right">
                                            <button
                                                v-if="!doc.system_doc"
                                                :disabled="wait"
                                                class="btn btn-outline-danger btn-sm"
                                                @click="deleteDoc(doc)">
                                                Delete
                                            </button>
                                            <span v-else class="badge badge-secondary">System Generated File</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div> <!-- end collapse -->
                </div> <!-- end card -->
            </div> <!-- end accordion -->
        </section>

        <button v-if="allowEditing" @click="updateDocuments()" class="btn btn-outline-primary">Submit Updates</button>

    </div>
</template>

<style scoped>
.row.g-1 {
    margin-left: -0.5rem; /* Adjust the negative margin to reduce the gap */
    margin-right: -0.5rem;
}

</style>
